/**
 * Where the console keeps what it was given at Connect.
 *
 * The key lives in sessionStorage only: it goes when the tab closes, and no
 * other tab or later visit sees it. The base URL is not a secret, so it is
 * remembered in localStorage for next time. Storage can be missing or
 * throw (a private window, blocked site data); then nothing is remembered.
 */

import { Api, normalizeBase } from './api';

const KEY = 'schedule-bit.console.key';
const BASE = 'schedule-bit.console.base';

const read = (store: () => Storage, k: string): string | null => {
  try {
    return store().getItem(k);
  } catch {
    return null;
  }
};
const write = (store: () => Storage, k: string, v: string | null): void => {
  try {
    if (v === null) store().removeItem(k);
    else store().setItem(k, v);
  } catch {
    // Nowhere to keep it; the console still works for this page's life.
  }
};

/** The API the console was built to point at; `CONSOLE_API_BASE` at build time. */
declare const CONSOLE_API_BASE: string;
export const defaultBase = (): string => {
  const built = typeof CONSOLE_API_BASE === 'string' ? CONSOLE_API_BASE : '';
  return read(() => localStorage, BASE) ?? built;
};

export const savedApi = (): Api | null => {
  const key = read(() => sessionStorage, KEY);
  const base = read(() => localStorage, BASE);
  return key && base ? new Api(base, key) : null;
};

export const saveSession = (base: string, key: string): Api => {
  write(() => localStorage, BASE, normalizeBase(base));
  write(() => sessionStorage, KEY, key);
  return new Api(base, key);
};

export const forgetKey = (): void => write(() => sessionStorage, KEY, null);
